<template>
  <div>
    <div v-if="chipStyle === 'inline' && (userSetting || unsavedTags) && !readonly"
      :style="autocompleteFilterChipStyle">
      <v-chip v-for="tag in selectedTags" :key="tag.tagId"
        @click:close="tagClicked(tag)"
        x-small
        close
        class="mr-1 mb-1 autocomplete-filter">
        <span>{{tag.value}}</span>
      </v-chip>
    </div>
    <v-autocomplete
      :id="elementId"
      class="has-filter-chips"
      color="black"
      item-color="primary"
      ref="accountField"
      :disabled="disabled"
      :item-disabled="account => account.disabled"
      v-model="chosenAccount"
      :label="title"
      :loading="loading"
      :items="decoratedAccounts"
      autocomplete="chrome-off"
      :clearable="clearable && !readonly"
      @click:clear="cleared = true"
      item-text="decoratedName"
      return-object
      :filter="(item, queryText, itemText) => itemText.toLocaleLowerCase().indexOf(queryText.toLocaleLowerCase()) > -1"
      :hint="hint"
      :persistent-hint="!readonly && hint !== ''"
      :single-line="singleLine"
      :rules="required ? [rules.required] : undefined"
      :readonly="readonly"
      :append-icon="readonly ? '' : '$dropdown'"
    >
      <template #selection="{item}">
        {{ item.name }}
        <Icon
          v-if="ticketBookTooltipByAccountId.has(item.accountId)"
          icon="mdi-book-open-variant"
          iconColor="info"
          margin="ml-1"
          :tooltipText="ticketBookTooltipByAccountId.get(item.accountId)"/>
        <Icon
          v-else-if="nonTicketBookAccountTooltip && ticketBookTooltipByAccountId.size > 0"
          icon="mdi-book-open-variant"
          iconColor="error"
          margin="ml-1"
          :tooltipText="nonTicketBookAccountTooltip"/>
        <slot name="selection" v-bind="{item}"/>
      </template>
      <template #item="{item}">
        {{ item.name }}
        <Icon
          v-if="ticketBookTooltipByAccountId.has(item.accountId)"
          icon="mdi-book-open-variant"
          iconColor="info"
          margin="ml-1"
          :tooltipText="ticketBookTooltipByAccountId.get(item.accountId)"/>
        <slot name="item" v-bind="{item}"/>
      </template>
      <template #prepend-inner>
        <v-icon>mdi-account</v-icon>
      </template>
      <template #append>
        <slot name="append"/>
      </template>
      <template #prepend>
        <slot name="prepend"/>
      </template>
      <template #append-outer>
        <div v-if="hasDisabledAccounts">
          <v-tooltip bottom color="info">
            <template #activator="{ on, attrs }">
              <v-icon
                v-on="on"
                v-bind="attrs"
                color="info">
                mdi-alert-circle-outline
              </v-icon>
            </template>
            <span class="subtitle-1 white--text" style="white-space: pre-line">{{ tooltipText }}</span>
          </v-tooltip>
        </div>
        <v-icon v-if="!hideRefresh && !readonly" @click="refreshAccounts(true)" color="primary" tabindex="-1">mdi-refresh</v-icon>
        <v-menu
        v-if="(userSetting || unsavedTags) && !readonly"
        v-model="showMenu"
        offset-y
        style="max-width: 600px"
        data-testid="account-tag"
        :close-on-content-click="false"
        >
          <template #activator="{on}">
            <v-icon color="primary" v-on="on" :tabIndex="tabFilter ? '' : '-1'">{{ tagFilterModeIcon }}</v-icon>
          </template>
          <v-list v-if="accountTags && accountTags.length > 0">
            <TagFilterModeToggle v-model="tagFilterMode" :userSetting="userSetting"/>
            <v-divider/>
            <v-list-item @click="tagClicked(tag)" v-for="tag in accountTags" :key="tag.tagId">
              <v-list-item-action>
                <v-icon :color="selectedTags.some(t => t.tagId === tag.tagId) ? 'primary' : ''">{{ selectedTags.some(t => t.tagId === tag.tagId) ? 'mdi-checkbox-marked' : 'mdi-checkbox-blank-outline'}}</v-icon>
              </v-list-item-action>
              <v-list-item-title>
                {{tag.value}}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-menu>
        <slot name="append-outer"/>
      </template>
    </v-autocomplete>

    <div v-if="chipStyle === 'below'" class="mt-2">
      <v-chip v-for="tag in selectedTags" :key="tag.tagId"
        close @click:close="tagClicked(tag)"
      >
        {{tag.value}}
      </v-chip>
    </div>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { isEqual } from 'lodash'
import { SETTING_TYPES, SETTING_KEYS } from '@/utils/UserSettings.js'
import { CertificationStatus, TagFilterMode } from '@/utils/Enumerations.js'
import { autocompleteFilterChipStyle, accountMatchesTagFilter } from '@/utils/componentHelpers'
import fieldRules from '@/utils/rules.js'
import { milesBetween } from '@/utils/Distance.js'
import { routeMilesFromOrigin } from '@/utils/map/RouteDistance.js'

export default {
  name: 'AccountAutocomplete',

  components: {
    Icon: () => import('@/components/helper/Icon.vue'),
    TagFilterModeToggle: () => import('@/components/autocomplete/TagFilterModeToggle.vue')
  },

  props: {
    accountId: { type: Number },
    userSetting: { type: String, default: undefined },
    clearable: { type: Boolean, default: false },
    title: { default: 'Account', type: String },
    hideRefresh: { type: Boolean, default: false },
    showCertified: { type: Boolean, default: false },
    propAccounts: { type: Array, default: () => [] },
    onlyInternal: { type: Boolean, default: false },
    onlyExternal: { type: Boolean, default: false },
    hint: { type: String, default: '' },
    unsavedTags: { type: Boolean, default: false },
    chipStyle: { type: String, default: 'inline' },
    disabled: { type: Boolean, default: false },
    prohibitedSet: { type: Set, default: undefined },
    singleLine: { type: Boolean, default: false },
    isQuickAssign: { type: Boolean, default: false },
    propSelectedTags: { type: Array, required: false },
    fetchTagsAndAccounts: { type: Boolean, default: true },
    propLoading: { type: Boolean, required: false, default: false },
    noSelection: { type: Boolean, required: false, default: false },
    elementId: { type: String, required: false, default: undefined },
    required: { type: Boolean, default: false },
    itemDisabled: { type: Function, required: false, default: _ => false },
    disabledItemTooltip: { type: String, required: false, default: undefined },
    readonly: { type: Boolean, default: false },
    tabFilter: { type: Boolean, default: true },
    originSpot: { type: Object, default: null },
    ticketBookAccounts: { type: Array, default: () => [] },
    nonTicketBookAccountTooltip: { type: String, default: undefined }
  },

  data: () => ({
    rules: fieldRules.rules,
    autocompleteFilterChipStyle,
    initialAccount: null,
    chosenAccount: null,
    tags: [],
    loading: false,
    cleared: false,
    initialAccountSet: false,
    selectedTags: [],
    tagFilterMode: TagFilterMode.Any.value,
    tagIdsFromStorage: [],
    showMenu: false,
    certifiedText: '(Certified)',
    routeMilesByAccountId: {}
  }),

  watch: {
    chosenAccount (account) {
      this.$emit('account-chosen', account)
    },

    tagIdsFromStorage (t) {
      this.initializeSelectedTags(t)
    },

    storedTagIds (tagIds) {
      if (this.userSetting && !isEqual(tagIds, this.tagIdsFromStorage)) this.tagIdsFromStorage = tagIds
    },

    storedTagFilterMode (mode) {
      if (this.userSetting) this.tagFilterMode = mode
    },

    accountTags () {
      this.initializeSelectedTags(this.tagIdsFromStorage)
    },

    onlyInternal () {
      this.refreshAccounts()
    },

    accounts (accounts) {
      if (this.chosenAccount && !accounts.find(a => a.accountId === this.chosenAccount.accountId)) this.chosenAccount = undefined
    },

    accountId (id) {
      this.chosenAccount = this.decoratedAccounts.find(a => a.accountId === id)
    },

    noSelection (value) {
      if (value) this.chosenAccount = undefined
    },

    routeMatrixSignature: {
      handler: 'loadRouteDistances',
      immediate: true
    }
  },

  computed: {
    ...mapGetters('account', ['allAccounts']),
    ...mapGetters('tags', ['accountTags']),
    ...mapGetters('user-settings', ['mutatedUserSettings']),

    decoratedAccounts () {
      const decorated = this.accounts
        .map(account => ({
          ...account,
          decoratedName: (this.showCertified && CertificationStatus.isCertified(account?.certificationStatus))
            ? `${account.name} ${this.certifiedText}`
            : account?.name,
          disabled: this.itemDisabled(account)
        }))

      if (!this.originSpotValid) {
        if (this.ticketBookTooltipByAccountId.size === 0) return decorated
        const isTicketBookAccount = account => this.ticketBookTooltipByAccountId.has(account.accountId)
        return decorated.sort((a, b) => isTicketBookAccount(b) - isTicketBookAccount(a))
      }

      return decorated
        .map(account => ({
          ...account,
          distanceFromOrigin: milesBetween(this.originSpot, { latitude: account.receiverSpotLatitude, longitude: account.receiverSpotLongitude }),
          routeDistanceFromOrigin: this.routeMilesByAccountId[account.accountId]
        }))
        .sort((a, b) => (a.distanceFromOrigin ?? Infinity) - (b.distanceFromOrigin ?? Infinity))
    },

    originSpotValid () {
      return typeof this.originSpot?.latitude === 'number' && typeof this.originSpot?.longitude === 'number'
    },

    ticketBookTooltipByAccountId () {
      return new Map(this.ticketBookAccounts.map(tba => [tba.accountId, tba.tooltipText]))
    },

    routeMatrixTargets () {
      if (!this.originSpotValid) return []
      return this.accounts.filter(a => typeof a?.receiverSpotLatitude === 'number' && typeof a?.receiverSpotLongitude === 'number')
    },

    routeMatrixSignature () {
      if (this.routeMatrixTargets.length === 0) return null
      const ids = this.routeMatrixTargets.map(a => a.accountId).sort((a, b) => a - b).join(',')
      return `${this.originSpot.latitude},${this.originSpot.longitude}|${ids}`
    },

    accounts () {
      const allAccounts = this.propAccounts && this.propAccounts.length > 0
        ? this.propAccounts
        : this.initialAccount
          ? [...new Set([...this.allAccounts, this.initialAccount])]
          : this.allAccounts
      return this.filterAccounts(allAccounts)
    },

    hasDisabledAccounts () {
      return this.decoratedAccounts && this.decoratedAccounts.some(account => account.disabled === true)
    },

    tooltipText () {
      return this.disabledItemTooltip ?? this.$t('disabledAccounts')
    },

    storedTagIds () {
      switch (this.userSetting) {
        case 'contractAccount':
          return this.mutatedUserSettings[SETTING_TYPES.CONTRACT_FORM][SETTING_KEYS.ACCOUNT_AUTOCOMPLETE]
        case 'contractDestinationAccount':
          return this.mutatedUserSettings[SETTING_TYPES.CONTRACT_FORM][SETTING_KEYS.DESTINATION_AUTOCOMPLETE]
        case 'contractFromAccount':
          return this.mutatedUserSettings[SETTING_TYPES.CONTRACT_FORM][SETTING_KEYS.FROM_ACCOUNT_AUTOCOMPLETE]
        case 'activityAccount':
          return this.mutatedUserSettings[SETTING_TYPES.ACTIVITY_FORM][SETTING_KEYS.ACCOUNT_AUTOCOMPLETE]
        case 'defaultLoggerAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.DEFAULT_ACCOUNT_AUTOCOMPLETE]
        case 'landOwnerAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.LAND_OWNER_ACCOUNT_AUTOCOMPLETE]
        case 'driverAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.DRIVER_ACCOUNT_AUTOCOMPLETE]
        case 'haulerAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.HAULER_ACCOUNT_AUTOCOMPLETE]
        case 'supplierAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.SUPPLIER_ACCOUNT_AUTOCOMPLETE]
        case 'tractContractAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.TRACT_CONTRACT_ACCOUNT_AUTOCOMPLETE]
        case 'consultingForesterAccountAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.CONSULTING_FORESTER_ACCOUNT_AUTOCOMPLETE]
        case 'tractDestinationAutocomplete':
          return this.mutatedUserSettings[SETTING_TYPES.TRACT_FORM][SETTING_KEYS.TRACT_DESTINATION_AUTOCOMPLETE]
        default:
          return []
      }
    },

    storedTagFilterMode () {
      return this.mutatedUserSettings[SETTING_TYPES.AUTOCOMPLETE][SETTING_KEYS.ACCOUNT_AUTOCOMPLETE_TAG_FILTER_MODES][this.userSetting] ?? TagFilterMode.Any.value
    },

    tagFilterModeIcon () {
      return TagFilterMode.forInt(this.tagFilterMode).details.icon
    }
  },

  created () {
    this.initialize(this.accountId)
  },

  methods: {
    ...mapActions('account', ['fetchAccounts', 'fetchAccountById']),
    ...mapActions('tags', ['fetchAccountTags']),
    ...mapMutations('user-settings', ['mutateUserSetting']),

    focus () {
      if (this.$refs.accountField) this.$refs.accountField.focus()
    },

    async loadRouteDistances () {
      const signature = this.routeMatrixSignature
      if (!signature) {
        this.routeMilesByAccountId = {}
        return
      }
      const targets = this.routeMatrixTargets
      const miles = await routeMilesFromOrigin(
        this.originSpot,
        targets.map(a => ({ latitude: a.receiverSpotLatitude, longitude: a.receiverSpotLongitude }))
      )
      if (signature !== this.routeMatrixSignature) return
      this.routeMilesByAccountId = Object.fromEntries(
        targets.map((a, i) => [a.accountId, miles[i]]).filter(([, m]) => m !== undefined)
      )
    },

    async initialize (accountId) {
      try {
        this.loading = true
        await this.getTags()
        await this.getAccounts(accountId)
        await this.setInitialAccount()
      } finally {
        this.loading = false
      }
    },

    async refreshAccounts () {
      try {
        this.loading = true
        await this.getAccounts(this.initialAccount?.accountId, true)
      } finally {
        this.loading = false
      }
    },

    tagClicked (tag) {
      const tagIndex = this.selectedTags.findIndex(t => t.tagId === tag.tagId)
      if (tagIndex === -1) {
        this.selectedTags.push(tag)
      } else {
        this.selectedTags.splice(tagIndex, 1)
      }

      if (this.userSetting) {
        this.setUserSettings()
      }
    },

    async getTags () {
      if (this.fetchTagsAndAccounts) await this.fetchAccountTags()
      if (this.userSetting) {
        this.checkUserSettings()
      } else if (this.propSelectedTags) {
        this.selectedTags = this.propSelectedTags
      }
    },

    async getAccounts (accountId, forceFetch = false) {
      if (this.fetchTagsAndAccounts || forceFetch) await this.fetchAccounts()

      if (accountId && accountId !== -1) {
        this.initialAccount = this.allAccounts.find(a => a.accountId === accountId)
        if (!this.initialAccount) {
          const account = await this.fetchAccountById(accountId)
          this.initialAccount = account
        }
      }
    },

    initializeSelectedTags (tagIds) {
      if (this.accountTags && this.accountTags.length > 0) this.selectedTags = tagIds.map(id => this.accountTags.find(at => at.tagId === id)).filter(Boolean)
    },

    filterAccounts (accounts) {
      const accountClause = (a) => {
        const violatesInternalRequirement = a.isExternal && this.onlyInternal
        const violatesExternalRequirement = !a.isExternal && this.onlyExternal

        const meetsTagRequirement = accountMatchesTagFilter(a.accountTags, this.selectedTags, this.tagFilterMode)

        const doesNotHaveProhibitedId = !this.prohibitedSet || !this.prohibitedSet.has(a.accountId)

        const isInitialAccount = this.initialAccount && this.initialAccount.accountId === a.accountId

        return (!violatesInternalRequirement && !violatesExternalRequirement && meetsTagRequirement && doesNotHaveProhibitedId) || isInitialAccount
      }
      return accounts.filter(accountClause)
    },

    checkUserSettings () {
      this.tagIdsFromStorage = this.storedTagIds
      this.tagFilterMode = this.storedTagFilterMode
    },

    setUserSettings () {
      let newSetting
      const tagIds = this.selectedTags.map(tag => tag.tagId)

      const createMutatedSetting = (type, key, value) => ({ type, key, value })

      switch (this.userSetting) {
        case 'contractAccount':
          newSetting = createMutatedSetting(SETTING_TYPES.CONTRACT_FORM, SETTING_KEYS.ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'contractDestinationAccount':
          newSetting = createMutatedSetting(SETTING_TYPES.CONTRACT_FORM, SETTING_KEYS.DESTINATION_AUTOCOMPLETE, tagIds)
          break
        case 'contractFromAccount':
          newSetting = createMutatedSetting(SETTING_TYPES.CONTRACT_FORM, SETTING_KEYS.FROM_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'activityAccount':
          newSetting = createMutatedSetting(SETTING_TYPES.ACTIVITY_FORM, SETTING_KEYS.ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'defaultLoggerAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.DEFAULT_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'landOwnerAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.LAND_OWNER_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'driverAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.DRIVER_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'haulerAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.HAULER_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'supplierAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.SUPPLIER_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'tractContractAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.TRACT_CONTRACT_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'consultingForesterAccountAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.CONSULTING_FORESTER_ACCOUNT_AUTOCOMPLETE, tagIds)
          break
        case 'tractDestinationAutocomplete':
          newSetting = createMutatedSetting(SETTING_TYPES.TRACT_FORM, SETTING_KEYS.TRACT_DESTINATION_AUTOCOMPLETE, tagIds)
          break
      }

      if (newSetting !== undefined) {
        this.mutateUserSetting(newSetting)
        this.tagIdsFromStorage = tagIds
      }
    },

    async setInitialAccount (force = false) {
      if (!force && (this.cleared || this.accountId === undefined || this.accountId === null)) {
        return
      }
      if (this.initialAccount !== null) {
        const inList = this.accounts.some(a => a?.accountId === this.initialAccount?.accountId)
        if (!inList && !this.isQuickAssign) {
          this.accounts.unshift(this.initialAccount)
        }
        this.chosenAccount = this.decoratedAccounts.find(a => a?.accountId === this.initialAccount?.accountId)
      }
    }
  }
}
</script>
